import React, { useEffect, useId, useRef, useState } from "react"
import cx from "classnames"
import styles from "@/components/core/SelectDropdown/index.module.scss"

const SelectDropdown = ({
  id: suppliedId,
  data = [],
  value,
  label = "",
  ariaLabel,
  ariaRequired,
  ariaStateText,
  showStateName,
  tabIndex = 0,
  customClass,
  showError,
  errorMessage,
  onChange,
  onClose,
  onBlur,
}) => {
  const generatedId = useId()
  const id = suppliedId || `select-${generatedId}`
  const [showList, setShowList] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const root = useRef(null)
  const trigger = useRef(null)
  const options = useRef([])
  const wasOpen = useRef(false)
  const search = useRef({ text: "", time: 0 })
  const optionValue = item =>
    ariaStateText ? (showStateName ? item.name : item.code) : item
  const optionText = item => String(ariaStateText ? item.name : item)
  const accessibleLabel = (label || ariaLabel || "Select an option")
    .replace(/\*/g, "")
    .trim()

  useEffect(() => {
    if (showList) options.current[activeIndex]?.focus()
  }, [showList, activeIndex])

  useEffect(() => {
    if (wasOpen.current && !showList) onClose?.()
    wasOpen.current = showList
  }, [showList, onClose])

  useEffect(() => {
    const outside = event => {
      if (!root.current?.contains(event.target)) setShowList(false)
    }
    document.addEventListener("mousedown", outside)
    return () => document.removeEventListener("mousedown", outside)
  }, [])

  const open = () => {
    if (!data.length) return
    setActiveIndex(
      Math.max(
        0,
        data.findIndex(item => optionValue(item) === value)
      )
    )
    setShowList(true)
  }
  const close = () => {
    setShowList(false)
    trigger.current?.focus()
  }
  const select = item => {
    onChange?.(optionValue(item))
    close()
  }
  const handleKeyDown = (event, index) => {
    const fromOption = index !== undefined
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault()
        if (!showList) open()
        else
          setActiveIndex(
            (activeIndex + (event.key === "ArrowDown" ? 1 : -1) + data.length) %
              data.length
          )
        break
      case "Home":
      case "End":
        if (!showList) return
        event.preventDefault()
        setActiveIndex(event.key === "Home" ? 0 : data.length - 1)
        break
      case "Enter":
      case " ":
        event.preventDefault()
        if (fromOption) select(data[index])
        else if (showList) close()
        else open()
        break
      case "Escape":
        if (showList) {
          event.preventDefault()
          event.stopPropagation()
          close()
        }
        break
      case "Tab":
        if (showList) close()
        break
      default: {
        if (
          event.key.length !== 1 ||
          event.ctrlKey ||
          event.metaKey ||
          event.altKey
        )
          return
        const now = Date.now()
        const text =
          (now - search.current.time < 700 ? search.current.text : "") +
          event.key.toLowerCase()
        search.current = { text, time: now }
        const match = data.findIndex(item =>
          optionText(item).toLowerCase().startsWith(text)
        )
        if (match >= 0) {
          event.preventDefault()
          setActiveIndex(match)
          setShowList(true)
        }
      }
    }
  }

  return (
    <div className={styles.wrapper} ref={root}>
      <div
        className={cx("select-dropdown", customClass, {
          "dropdown--open": showList,
        })}
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setShowList(false)
            onBlur?.()
          }
        }}
      >
        <div
          id={id}
          ref={trigger}
          className={`select-dropdown__field ${
            showError ? "--with-error" : ""
          }`}
          onClick={() => (showList ? close() : open())}
          onKeyDown={event => handleKeyDown(event)}
          tabIndex={tabIndex}
          role="combobox"
          aria-controls={`${id}-list`}
          aria-expanded={showList}
          aria-haspopup="listbox"
          aria-label={accessibleLabel}
          aria-required={ariaRequired ?? label.includes("*")}
          aria-invalid={!!showError}
          aria-describedby={
            [
              value ? `${id}-selected` : "",
              showError && errorMessage ? `${id}-error` : "",
            ]
              .filter(Boolean)
              .join(" ") || undefined
          }
        >
          <div className="select-dropdown__display">
            <span
              aria-hidden="true"
              className={`select-dropdown__placeholder ${
                value ? "--label" : ""
              }`}
            >
              {label}
            </span>
            {value && (
              <span id={`${id}-selected`} className="select-dropdown__value">
                {value}
              </span>
            )}
          </div>
          <div
            className={`select-dropdown__icon ${showList ? "--open" : ""}`}
            aria-hidden="true"
          >
            <img src="/icons/down-icon.svg" alt="" />
          </div>
        </div>
        <ul
          id={`${id}-list`}
          className="select-dropdown__dropdown"
          role="listbox"
          aria-label={accessibleLabel}
          hidden={!showList}
          style={!showList ? { display: "none" } : undefined}
          data-testid="select-dropdown"
        >
          {data.map((item, index) => (
            <li
              key={index}
              ref={element => {
                options.current[index] = element
              }}
              className={cx("select-dropdown__list-item", {
                "selected-item": optionValue(item) === value,
              })}
              role="option"
              aria-selected={optionValue(item) === value}
              tabIndex={showList && activeIndex === index ? 0 : -1}
              onFocus={() => setActiveIndex(index)}
              onClick={() => select(item)}
              onKeyDown={event => handleKeyDown(event, index)}
            >
              <span className="select-dropdown__list-value">
                {optionValue(item)}
              </span>
            </li>
          ))}
        </ul>
        {showError && errorMessage && (
          <span
            id={`${id}-error`}
            role="alert"
            className="select-dropdown__error-message"
          >
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  )
}

export default SelectDropdown
